import { useEffect, useState } from "react";
import { Mail, Phone, MessageCircle, MapPin, Navigation, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { KineticLines, Reveal } from "@/components/Motion";
import { SITE } from "@/config/site";
import api from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

export default function Contact() {
  const [settings, setSettings] = useState({});
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      trackEvent("contact_form_submit");
      setForm({ name: "", email: "", company: "", phone: "", subject: "", message: "", website: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const contactSchema = {
    "@context": "https://schema.org", "@type": "ContactPage",
    name: `Contact ${SITE.brand}`, url: `${SITE.url}/contact`,
  };

  return (
    <div data-testid="contact-page">
      <SEO title="Contact" path="/contact" description="Contact STJ Innovations (STJ Innovations LLP), Plot No. 543/1, Mundka, New Delhi – 110041, India — cleanroom equipment and precision engineering enquiries." jsonLd={contactSchema} />
      <section className="blueprint-dark bg-ink">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <Breadcrumbs dark items={[{ label: "Contact" }]} />
          <p className="eyebrow eyebrow-light">Contact</p>
          <KineticLines lines={["Talk to STJ"]}
            className="mt-5 font-display text-4xl font-medium leading-[1.06] tracking-tight text-white sm:text-5xl lg:text-6xl" />
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
            For product enquiries, project discussions and international business development — reach our team directly.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-2xl font-semibold text-ink">{SITE.brand}</h2>
              <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-steel">{SITE.legalName}</p>
              <ul className="mt-8 space-y-5">
                <li className="flex items-start gap-4 border border-line p-5">
                  <MapPin size={18} className="mt-0.5 shrink-0 text-accent" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Location</p>
                    <p className="mt-1 text-sm text-ink" data-testid="contact-address">{settings.address || "India"}</p>
                    <a href={settings.maps_url || "https://maps.app.goo.gl/3vUp1vknBQThs7i47"}
                      target="_blank" rel="noopener noreferrer" data-testid="contact-maps-link"
                      onClick={() => trackEvent("maps_click")}
                      className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-accent transition-colors hover:text-ink">
                      <Navigation size={12} /> View on Google Maps
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 border border-line p-5">
                  <Mail size={18} className="mt-0.5 shrink-0 text-accent" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Email</p>
                    <a href={`mailto:${settings.email}`} data-testid="contact-email-link"
                      onClick={() => trackEvent("email_click")}
                      className="mt-1 block text-sm font-medium text-ink hover:text-accent">
                      {settings.email || "info@stjinnovations.com"}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4 border border-line p-5">
                  <Phone size={18} className="mt-0.5 shrink-0 text-accent" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">Phone</p>
                    {(settings.phones?.length ? settings.phones : [settings.phone].filter(Boolean)).map((p, i) => (
                      <a key={p} href={`tel:${p.replace(/[^+\d]/g, "")}`} data-testid={i === 0 ? "contact-phone-link" : `contact-phone-link-${i + 1}`}
                        onClick={() => trackEvent("phone_click")}
                        className="mt-1 block text-sm font-medium text-ink hover:text-accent">
                        {p}
                      </a>
                    ))}
                  </div>
                </li>
                <li className="flex items-start gap-4 border border-line p-5">
                  <MessageCircle size={18} className="mt-0.5 shrink-0 text-accent" />
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">WhatsApp</p>
                    <a href={`https://wa.me/${(settings.whatsapp || "").replace(/[^0-9]/g, "")}`} data-testid="contact-whatsapp-link"
                      onClick={() => trackEvent("whatsapp_click")} target="_blank" rel="noopener noreferrer"
                      className="mt-1 block text-sm font-medium text-ink hover:text-accent">
                      {settings.whatsapp || "+91-9560040097"} — business enquiries
                    </a>
                  </div>
                </li>
              </ul>
              <div className="mt-6 border border-line" data-testid="contact-map-embed">
                <iframe
                  title="STJ Innovations LLP — Plot No. 543/1, Mundka, New Delhi"
                  src="https://maps.google.com/maps?q=28.683607,77.034897&z=16&output=embed"
                  className="h-64 w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
                <a href={settings.maps_url || "https://maps.app.goo.gl/3vUp1vknBQThs7i47"}
                  target="_blank" rel="noopener noreferrer" data-testid="contact-directions-button"
                  onClick={() => trackEvent("maps_click")}
                  className="flex items-center justify-center gap-2 bg-ink px-4 py-3.5 text-[13px] font-semibold text-white transition-colors hover:bg-accent">
                  <Navigation size={14} /> Get Directions — STJ Innovations LLP, Mundka
                </a>
              </div>
              <p className="mt-6 text-xs leading-relaxed text-slate-400">
                We respond to business enquiries on working days. For technical requirements, the Request a Quote form captures the detail our engineers need.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form onSubmit={submit} className="border border-line bg-mist p-7 sm:p-10" data-testid="contact-form">
                <h2 className="font-display text-2xl font-semibold text-ink">Send a Message</h2>
                <div className="mt-7 grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="cf-name" className="label-field">Name *</label>
                    <input id="cf-name" data-testid="contact-name-input" required className="input-field" value={form.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label htmlFor="cf-email" className="label-field">Work Email *</label>
                    <input id="cf-email" data-testid="contact-email-input" type="email" required className="input-field" value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label htmlFor="cf-company" className="label-field">Company</label>
                    <input id="cf-company" data-testid="contact-company-input" className="input-field" value={form.company} onChange={set("company")} />
                  </div>
                  <div>
                    <label htmlFor="cf-phone" className="label-field">Phone</label>
                    <input id="cf-phone" data-testid="contact-phone-input" type="tel" className="input-field" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-subject" className="label-field">Subject</label>
                    <input id="cf-subject" data-testid="contact-subject-input" className="input-field" value={form.subject} onChange={set("subject")} />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="cf-message" className="label-field">Message</label>
                    <textarea id="cf-message" data-testid="contact-message-input" rows={5} className="input-field" value={form.message} onChange={set("message")} />
                  </div>
                  <input type="text" name="website" value={form.website} onChange={set("website")}
                    tabIndex={-1} autoComplete="off" aria-hidden="true"
                    className="absolute -left-[9999px] h-0 w-0 opacity-0" />
                </div>
                <button type="submit" data-testid="contact-submit-button" disabled={sending}
                  className="mt-7 inline-flex items-center gap-2 bg-accent px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-accent-dark disabled:opacity-60">
                  {sending ? "Sending…" : "Send Message"} <Send size={14} />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
